/**
 * @description Namespace representing the different statuses a bet can have.
 * Each status is associated with a unique numeric identifier using BigInt (n suffix).
 *
 * @property {bigint} Open - Status ID 1: Represents a bet that is still active and open
 * @property {bigint} Canceled - Status ID 2: Represents a bet that has been canceled
 * @property {bigint} Refunded - Status ID 3: Represents a bet where the amount has been refunded
 * @property {bigint} Won - Status ID 4: Represents a bet that has been won
 * @property {bigint} Lost - Status ID 5: Represents a bet that has been lost
 * @property {bigint} CashedOut - Status ID 6: Represents a bet that has been cashed out early
 * @property {bigint} HalfWon - Status ID 7: Represents a bet that has been partially won
 * @property {bigint} HalfLost - Status ID 8: Represents a bet that has been partially lost
 */
export namespace BetStatusId {
  // Status for bets that are still active and awaiting results
  export const Open = 1n;

  // Status for bets that have been canceled by the system or admin
  export const Canceled = 2n;

  // Status for bets where the amount has been returned to the user
  export const Refunded = 3n;

  // Status for bets that have been successful
  export const Won = 4n;

  // Status for bets that have been unsuccessful
  export const Lost = 5n;

  // Status for bets that have been manually closed by the user before completion
  export const CashedOut = 6n;

  // Status for bets that have been partially successful
  export const HalfWon = 7n;

  // Status for bets that have been partially unsuccessful
  export const HalfLost = 8n;
}

/**
 * @description Type representing the possible values of BetStatusId.
 * This type is derived from the keys of the BetStatusId namespace.
 *
 * @type {bigint} - A BigInt value representing one of the bet status identifiers
 */
export type BetStatusIdType = (typeof BetStatusId)[keyof typeof BetStatusId];
